import { createLogger } from '@/lib/logger'
import {
  desktopSecureStorage,
  getDesktopDeepLinkScheme,
  isTauri,
} from '@/lib/utils/tauri'

import type { AuthDataResponse, CreateUserDTO, User } from '@scrambl/shared'

/**
 * Authentication API service for managing user authentication flows.
 * @module lib/api/auth.api
 */

import { apiClient, ApiError } from './api-client'
import { buildQueryString } from './base-api'
import { createSingletonApi } from './singleton-factory'

const logger = createLogger('lib/api/auth.api')

function isUserData(data: unknown): data is User {
  return Boolean(
    data && typeof data === 'object' && 'id' in data && 'email' in data,
  )
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}
export interface ExtendedAuthDataResponse extends AuthDataResponse {
  redirect?: string
}

export interface CurrentUserResponse {
  success: boolean
  message: string
  data: User
}

interface AdminAccessResponse {
  success: boolean
  data: {
    allowed: boolean
  }
}

class AuthApiService {
  /**
   * Initiates login flow with email
   * @param email - User's email address
   * @returns Auth response with session
   */
  async login(email: string): Promise<AuthDataResponse> {
    const normalizedEmail = normalizeEmail(email)
    return apiClient<AuthDataResponse>('/auth/login', {
      method: 'POST',
      body: { email: normalizedEmail },
      withAuth: false,
    })
  }

  /**
   * Verifies login with code received via email
   * @param email - User's email
   * @param code - Verification code
   * @param session - Session token
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async verifyLogin(
    email: string,
    code: string,
    session: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<AuthDataResponse> {
    const headers: Record<string, string> = {}
    const normalizedEmail = normalizeEmail(email)

    // For desktop, we need to request tokens like mobile does
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : 'web')
    headers['x-platform'] = effectivePlatform

    const response = await apiClient<AuthDataResponse>('/auth/verify-login', {
      method: 'POST',
      body: { email: normalizedEmail, code, session },
      withAuth: false,
      headers,
    })

    // If running in desktop and we got tokens, save them
    if (isTauri() && response.success && response.tokens) {
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          email: normalizedEmail,
          user: isUserData(response.data) ? response.data : undefined,
        },
      )
    }

    return response
  }

  /**
   * Logs in with email and password.
   * @param email - User's email
   * @param password - User's password
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async passwordLogin(
    email: string,
    password: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<AuthDataResponse> {
    const headers: Record<string, string> = {}
    const normalizedEmail = normalizeEmail(email)
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : 'web')
    headers['x-platform'] = effectivePlatform

    const response = await apiClient<AuthDataResponse>('/auth/password-login', {
      method: 'POST',
      body: { email: normalizedEmail, password },
      withAuth: false,
      headers,
    })

    if (isTauri() && response.success && response.tokens) {
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          email: normalizedEmail,
          user: isUserData(response.data) ? response.data : undefined,
        },
      )
    }

    return response
  }

  async forgotPassword(email: string): Promise<AuthDataResponse> {
    return apiClient<AuthDataResponse>('/auth/password/forgot', {
      method: 'POST',
      body: { email: normalizeEmail(email) },
      withAuth: false,
    })
  }

  async resetPassword(
    email: string,
    code: string,
    newPassword: string,
  ): Promise<AuthDataResponse> {
    return apiClient<AuthDataResponse>('/auth/password/reset', {
      method: 'POST',
      body: { email: normalizeEmail(email), code, newPassword },
      withAuth: false,
    })
  }

  /**
   * Registers a new user
   * @param userData - User registration data
   * @returns Auth response
   */
  async register(userData: CreateUserDTO): Promise<AuthDataResponse> {
    const normalizedUserData = {
      ...userData,
      email: normalizeEmail(userData.email),
    }
    return apiClient<AuthDataResponse>('/auth/register', {
      method: 'POST',
      body: normalizedUserData,
      withAuth: false,
    })
  }

  /**
   * Confirms email with verification code
   * @param email - User's email
   * @param code - Verification code
   * @param inviteToken - Optional invite token
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async confirmSignUp(
    email: string,
    code: string,
    inviteToken?: string,
    platform?: 'web' | 'mobile' | 'desktop',
    registrationSession?: string,
  ): Promise<AuthDataResponse> {
    const headers: Record<string, string> = {}
    const normalizedEmail = normalizeEmail(email)
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : undefined)
    if (effectivePlatform) {
      headers['x-platform'] = effectivePlatform
    }

    const response = await apiClient<AuthDataResponse>('/auth/confirm', {
      method: 'POST',
      body: { email: normalizedEmail, code, inviteToken, registrationSession },
      withAuth: false,
      headers,
    })

    if (isTauri() && response.success && response.tokens) {
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          email: normalizedEmail,
          user: isUserData(response.data) ? response.data : undefined,
        },
      )
    }

    return response
  }

  /**
   * Resends verification code
   * @param email - User's email
   * @returns Auth response
   */
  async resendCode(email: string): Promise<AuthDataResponse> {
    const normalizedEmail = normalizeEmail(email)
    return apiClient<AuthDataResponse>('/auth/resend-code', {
      method: 'POST',
      body: { email: normalizedEmail },
      withAuth: false,
    })
  }

  /**
   * Gets current authenticated user profile
   * @returns Current user data
   */
  async getCurrentUser(): Promise<CurrentUserResponse> {
    try {
      return await apiClient<CurrentUserResponse>('/auth/me', {
        method: 'GET',
        withAuth: false,
        silentAuthFailure: true,
      })
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      ) {
        return {
          success: false,
          message: 'Not authenticated',
          data: null as unknown as User,
        }
      }

      logger.error('[AuthApi] Error getting current user:', error)
      return {
        success: false,
        message: 'Error fetching user data',
        data: null as unknown as User,
      }
    }
  }

  async getAdminAccessStatus(): Promise<boolean> {
    try {
      const response = await apiClient<AdminAccessResponse>(
        '/auth/admin/access',
        {
          method: 'GET',
          withAuth: true,
        },
      )

      return Boolean(response.data?.allowed)
    } catch (error) {
      logger.warn('[AuthApi] Admin access check failed:', error)
      return false
    }
  }

  /**
   * Logs out the current user
   * @returns Auth response with redirect
   */
  async logout(): Promise<ExtendedAuthDataResponse> {
    try {
      const response = await apiClient<AuthDataResponse>('/auth/logout', {
        method: 'POST',
        withAuth: true,
      })

      // Clear desktop tokens on logout
      if (isTauri()) {
        await desktopSecureStorage.deleteTokens()
      }

      if (response.success) {
        return { ...response, redirect: '/login' }
      }

      return response
    } catch (error) {
      logger.error('[AuthApi] Logout error:', error)
      // Still clear tokens on error
      if (isTauri()) {
        await desktopSecureStorage.deleteTokens()
      }
      return {
        success: true,
        message: 'Logged out successfully',
        redirect: '/login',
      }
    }
  }

  /**
   * Checks if an email exists
   * @param email - Email to check
   * @param purpose - Purpose of check (login or register)
   * @param inviteToken - Optional invite token
   * @returns Whether email exists
   */
  async checkEmailExists(
    email: string,
    purpose?: 'login' | 'register',
    inviteToken?: string,
  ): Promise<{ exists: boolean }> {
    const queryString = buildQueryString({
      email: normalizeEmail(email),
      purpose,
      inviteToken,
    })
    return apiClient<{ exists: boolean }>(
      `/auth/check-email-exists${queryString}`,
      {
        method: 'GET',
        withAuth: false,
      },
    )
  }

  /**
   * Checks if email exists for registration flow
   * @param email - Email to check
   * @param inviteToken - Optional invite token
   * @returns Whether email exists
   */
  async checkEmailExistsForRegistration(
    email: string,
    inviteToken?: string,
  ): Promise<{ exists: boolean }> {
    return this.checkEmailExists(email, 'register', inviteToken)
  }

  /**
   * Refreshes the authentication token
   * @returns Auth response
   */
  async refreshToken(): Promise<ExtendedAuthDataResponse> {
    try {
      // For desktop, include refresh token in header
      const headers: Record<string, string> = {}
      if (isTauri()) {
        const tokens = await desktopSecureStorage.getTokens()
        if (tokens?.refreshToken) {
          headers['Authorization'] = `Bearer ${tokens.refreshToken}`
        }
        headers['x-platform'] = 'desktop'
      }

      const response = await apiClient<AuthDataResponse>('/auth/refresh', {
        method: 'POST',
        withAuth: true,
        headers,
      })

      // If running in desktop and we got new tokens, save them
      if (isTauri() && response.success && response.tokens) {
        await desktopSecureStorage.saveTokens(
          {
            accessToken: response.tokens.accessToken,
            refreshToken: response.tokens.refreshToken,
            idToken: response.tokens.idToken,
          },
          {
            user: isUserData(response.data) ? response.data : undefined,
          },
        )
      }

      return response
    } catch (error) {
      logger.error('[AuthApi] Token refresh error:', error)
      return {
        success: false,
        message: 'Failed to refresh token',
        redirect: '/login',
      }
    }
  }

  /**
   * Gets a short-lived token for socket authentication
   * @returns Socket token
   */
  async getSocketToken(): Promise<{ token: string }> {
    return apiClient<{ token: string }>('/auth/socket-token', {
      method: 'GET',
      withAuth: true,
    })
  }

  /**
   * Gets Google OAuth login URL
   * @param inviteToken - Optional invite token
   * @param flow - Login or register flow
   * @param platform - Platform making the request
   * @param deepLinkScheme - Deep link scheme for desktop
   * @param forceNewAccount - Force showing account picker for adding new accounts
   * @returns Auth response with URL
   */
  async getGoogleLoginUrl(
    inviteToken?: string,
    flow?: 'login' | 'register',
    platform?: 'web' | 'mobile' | 'desktop',
    deepLinkScheme?: string,
    forceNewAccount?: boolean,
  ): Promise<AuthDataResponse> {
    // For desktop, pass the platform so the backend can include it in the state
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : undefined)

    // For desktop, also get the deep link scheme if not provided
    let effectiveScheme = deepLinkScheme
    if (!effectiveScheme && isTauri()) {
      effectiveScheme = await getDesktopDeepLinkScheme()
    }

    const queryString = buildQueryString({
      inviteToken,
      flow,
      forceNewAccount: forceNewAccount ? 'true' : undefined,
    })

    const headers: Record<string, string> = {}
    if (effectivePlatform) {
      headers['x-platform'] = effectivePlatform
    }
    if (effectiveScheme) {
      headers['x-deep-link-scheme'] = effectiveScheme
    }

    logger.info('[getGoogleLoginUrl] Request', {
      platform: effectivePlatform,
      deepLinkScheme: effectiveScheme,
      headers,
      queryString,
    })

    return apiClient<AuthDataResponse>(`/auth/google/login${queryString}`, {
      method: 'GET',
      withAuth: false,
      headers,
    })
  }

  /**
   * Handles Google OAuth callback
   * @param code - OAuth code
   * @param state - OAuth state
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async handleGoogleCallback(
    code: string,
    state: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<AuthDataResponse> {
    const queryString = buildQueryString({ code, state })
    const headers: Record<string, string> = {}

    // For desktop, we need to request tokens like mobile does
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : 'web')
    headers['x-platform'] = effectivePlatform

    const response = await apiClient<AuthDataResponse>(
      `/auth/callback${queryString}`,
      {
        method: 'GET',
        withAuth: false,
        headers,
      },
    )

    // If running in desktop and we got tokens, save them
    if (isTauri() && response.success && response.tokens) {
      await desktopSecureStorage.saveTokens(
        {
          accessToken: response.tokens.accessToken,
          refreshToken: response.tokens.refreshToken,
          idToken: response.tokens.idToken,
        },
        {
          user: isUserData(response.data) ? response.data : undefined,
        },
      )
    }

    return response
  }

  /**
   * Gets Microsoft OAuth login URL
   * @param accountType - 'work' for work/school accounts, 'personal' for personal accounts
   * @param inviteToken - Optional invite token
   * @param flow - Login or register flow
   * @param platform - Platform making the request
   * @param deepLinkScheme - Deep link scheme for desktop
   * @returns Auth response with URL
   */
  async getMicrosoftLoginUrl(
    accountType: 'personal' | 'work' = 'personal',
    inviteToken?: string,
    flow?: 'login' | 'register',
    platform?: 'web' | 'mobile' | 'desktop',
    deepLinkScheme?: string,
  ): Promise<AuthDataResponse> {
    // For desktop, pass the platform so the backend can include it in the state
    const effectivePlatform = platform ?? (isTauri() ? 'desktop' : undefined)

    // For desktop, also get the deep link scheme if not provided
    let effectiveScheme = deepLinkScheme
    if (!effectiveScheme && isTauri()) {
      effectiveScheme = await getDesktopDeepLinkScheme()
    }

    const queryString = buildQueryString({
      accountType,
      inviteToken,
      flow,
    })

    const headers: Record<string, string> = {}
    if (effectivePlatform) {
      headers['x-platform'] = effectivePlatform
    }
    if (effectiveScheme) {
      headers['x-deep-link-scheme'] = effectiveScheme
    }

    logger.info('[getMicrosoftLoginUrl] Request', {
      accountType,
      platform: effectivePlatform,
      deepLinkScheme: effectiveScheme,
      headers,
      queryString,
    })

    return apiClient<AuthDataResponse>(`/auth/microsoft/login${queryString}`, {
      method: 'GET',
      withAuth: false,
      headers,
    })
  }

  /**
   * Handles Microsoft OAuth callback
   * The Cognito code exchange is provider-agnostic, so this mirrors the Google
   * callback handling (same /auth/callback endpoint + desktop token storage).
   * @param code - OAuth code
   * @param state - OAuth state
   * @param platform - Optional platform (mobile/desktop to get tokens in response)
   * @returns Auth response
   */
  async handleMicrosoftCallback(
    code: string,
    state: string,
    platform?: 'web' | 'mobile' | 'desktop',
  ): Promise<AuthDataResponse> {
    return this.handleGoogleCallback(code, state, platform)
  }

  /**
   * Links existing account with Google
   * @param googleIdToken - Google ID token
   * @returns Auth response
   */
  async linkGoogleAccount(googleIdToken: string): Promise<AuthDataResponse> {
    return apiClient<AuthDataResponse>('/auth/google/link', {
      method: 'POST',
      body: { googleIdToken },
      withAuth: true,
    })
  }
}

export const authApi = createSingletonApi(AuthApiService)
